import { useState, useEffect } from 'react'
import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { StepIndicator } from './StepIndicator'
import { ScaffoldedOptions } from './ScaffoldedOptions'
import { GenerateQuestionModal } from './GenerateQuestionModal'
import { DECISION_TYPES } from '@/data/templates/decision-types'

interface PreReadComposerProps {
  projectId: string
  onComplete: (id: string) => void
  initialDecisionType?: string
}

const STEP_LABELS = ['Decision type', 'Stimuli', 'Segments', 'Review & launch']
const TOTAL_STEPS = 4

export function PreReadComposer({ projectId, onComplete, initialDecisionType }: PreReadComposerProps) {
  const [step, setStep] = useState(initialDecisionType ? 2 : 1)
  const [decisionType, setDecisionType] = useState(initialDecisionType ?? '')
  const [stimuliText, setStimuliText] = useState('')
  const [segmentsText, setSegmentsText] = useState('')
  const [launching, setLaunching] = useState(false)
  const [savedIndicator, setSavedIndicator] = useState(false)
  const [changeCount, setChangeCount] = useState(0)
  const [runError, setRunError] = useState<string | null>(null)
  const [showGenerateModal, setShowGenerateModal] = useState(false)

  // Track changes for saved indicator logic
  useEffect(() => {
    setChangeCount(c => c + 1)
  }, [decisionType, stimuliText, segmentsText])

  const selectedDT = DECISION_TYPES.find(dt => dt.id === decisionType)
  const researchQuestion = selectedDT?.step1_question ?? ''

  const stimuliChips = stimuliText
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)

  const segmentChips = segmentsText
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)

  const decisionTypeOptions = DECISION_TYPES.map(dt => ({
    value: dt.id,
    label: dt.name,
    rationale: dt.description,
  }))

  const handleRun = async () => {
    setLaunching(true)
    setRunError(null)
    try {
      const token = localStorage.getItem('echo_session')
      const res = await fetch('/api/v1/pre-reads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          project_id: projectId,
          research_question: researchQuestion,
          stimuli: stimuliChips,
          segment_ids: segmentChips,
          decision_type: decisionType,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string }
        throw new Error(err.error ?? `HTTP ${res.status}`)
      }
      const data = await res.json() as { pre_read_id: string }
      onComplete(data.pre_read_id)
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      setLaunching(false)
    }
  }

  const handleSaveDraft = () => {
    // Fire-and-forget draft save — surface indicator for 2 s
    setSavedIndicator(true)
    setTimeout(() => setSavedIndicator(false), 2000)
  }

  const handleGeneratedQuestion = (questionText: string, suggestedSegments: string[]) => {
    setShowGenerateModal(false)
    // Pre-fill stimuli with the question text and segments from the suggestion
    setStimuliText(questionText)
    if (suggestedSegments.length > 0) setSegmentsText(suggestedSegments.join(', '))
    // Jump to step 2 so the user can review + edit
    setStep(2)
  }

  const canAdvanceStep2 = stimuliChips.length >= 1
  const canAdvanceStep3 = segmentChips.length >= 1
  const canRun = decisionType && stimuliChips.length >= 1 && segmentChips.length >= 1

  return (
    <div className="flex min-h-[600px] rounded-xl border border-border-gray overflow-hidden">
      {/* ChatRail — left 40% */}
      <div className="w-[40%] border-r border-border-gray flex flex-col bg-white">
        <div className="px-5 pt-5 pb-4 border-b border-border-gray">
          <StepIndicator
            current={step}
            total={TOTAL_STEPS}
            stepLabel={STEP_LABELS[step - 1]}
          />
        </div>

        <div className="flex-1 p-5 overflow-y-auto space-y-4">
          {/* Step 1 — decision type */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-deep-ink font-medium">What kind of decision are you pre-reading?</p>
              <ScaffoldedOptions
                options={decisionTypeOptions}
                allowOther={false}
                selected={decisionType}
                onSelect={(value) => {
                  setDecisionType(value)
                  setStep(2)
                }}
              />
              <div className="pt-1 border-t border-border-gray">
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(true)}
                  className="flex items-center gap-1.5 text-xs text-echo-indigo hover:text-echo-indigo/80 transition-colors"
                >
                  <Sparkles size={13} />
                  Help me generate a question
                </button>
              </div>
            </div>
          )}

          {/* Step 2 — stimuli */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-deep-ink font-medium">What stimuli are you testing?</p>
              <p className="text-xs text-slate-500">Enter the names or short labels for each option, separated by commas.</p>
              <textarea
                className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-echo-indigo/30 resize-none"
                rows={3}
                placeholder="e.g. Option A, Option B, Control"
                value={stimuliText}
                onChange={e => setStimuliText(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(1)}
                >
                  ← Back
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canAdvanceStep2}
                  onClick={() => setStep(3)}
                >
                  Next →
                </Button>
              </div>
            </div>
          )}

          {/* Step 3 — segments */}
          {step === 3 && (
            <div className="space-y-3">
              <p className="text-sm text-deep-ink font-medium">Which audience segments should we test?</p>
              <p className="text-xs text-slate-500">Comma-separate segment names or IDs.</p>
              <textarea
                className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-echo-indigo/30 resize-none"
                rows={3}
                placeholder="e.g. New customers, Tenured, High-value"
                value={segmentsText}
                onChange={e => setSegmentsText(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(2)}
                >
                  ← Back
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canAdvanceStep3}
                  onClick={() => setStep(4)}
                >
                  Next →
                </Button>
              </div>
            </div>
          )}

          {/* Step 4 — summary */}
          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-deep-ink font-medium">Ready to launch</p>

              <div className="bg-soft-panel rounded-lg p-3 space-y-2 text-xs">
                <div>
                  <span className="text-slate-500">Decision type</span>
                  <p className="font-medium text-deep-ink mt-0.5">{selectedDT?.name ?? decisionType}</p>
                </div>
                <div>
                  <span className="text-slate-500">Research question</span>
                  <p className="italic text-slate-700 mt-0.5">"{researchQuestion}"</p>
                </div>
                <div>
                  <span className="text-slate-500">Stimuli</span>
                  <p className="font-medium text-deep-ink mt-0.5">{stimuliChips.join(' · ')}</p>
                </div>
                <div>
                  <span className="text-slate-500">Segments</span>
                  <p className="font-medium text-deep-ink mt-0.5">{segmentChips.join(' · ')}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(3)}
                >
                  ← Back
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canRun || launching}
                  onClick={handleRun}
                >
                  {launching ? 'Launching…' : 'Run it →'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SpecRail — right 60% */}
      <div className="flex-1 flex flex-col bg-slate-50">
        <div className="px-6 pt-5 pb-4 border-b border-border-gray">
          <p className="text-xs text-slate-400 mb-1">Research question</p>
          {researchQuestion ? (
            <p className="text-sm font-medium text-deep-ink">"{researchQuestion}"</p>
          ) : (
            <p className="text-sm text-slate-400 italic">Select a decision type to see your research question</p>
          )}
        </div>

        <div className="flex-1 p-6 space-y-5 overflow-y-auto">
          {/* Stimuli chips */}
          <div>
            <p className="text-xs text-slate-400 mb-2">Stimuli</p>
            {stimuliChips.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {stimuliChips.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2.5 py-1 rounded-md bg-white border border-border-gray text-xs font-medium text-deep-ink"
                  >
                    {s}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">None entered yet</p>
            )}
          </div>

          {/* Segment chips */}
          <div>
            <p className="text-xs text-slate-400 mb-2">Segments</p>
            {segmentChips.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {segmentChips.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2.5 py-1 rounded-md bg-echo-indigo/10 border border-echo-indigo/20 text-xs font-medium text-echo-indigo"
                  >
                    {s}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">None entered yet</p>
            )}
          </div>

          {/* Cost / time estimate */}
          <div className="rounded-lg border border-border-gray bg-white px-4 py-3">
            <p className="text-xs text-slate-500">
              Estimated runtime{' '}
              <span className="font-medium text-deep-ink">~38s</span>
              <span className="mx-2 text-slate-300">/</span>
              <span className="font-medium text-deep-ink">~$0.04</span>
            </p>
          </div>
        </div>

        {/* SpecRail footer actions */}
        <div className="px-6 py-4 border-t border-border-gray space-y-2">
          {runError && (
            <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{runError}</p>
          )}
          {!canRun && (
            <p className="text-xs text-slate-400">
              {!decisionType ? 'Select a decision type to continue.' : !stimuliChips.length ? 'Add at least one stimulus to continue.' : 'Add at least one segment to continue.'}
            </p>
          )}
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="md"
              className="bg-echo-indigo hover:bg-echo-indigo/90"
              disabled={!canRun || launching}
              onClick={handleRun}
            >
              {launching ? 'Launching…' : 'Run pre-read'}
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleSaveDraft}
            >
              Save draft
            </Button>
            {savedIndicator && (
              <span className={cn('text-xs text-slate-500 transition-opacity', savedIndicator ? 'opacity-100' : 'opacity-0')}>
                Draft saved
              </span>
            )}
          </div>
        </div>
      </div>

      {showGenerateModal && (
        <GenerateQuestionModal
          projectId={projectId}
          onSelect={handleGeneratedQuestion}
          onClose={() => setShowGenerateModal(false)}
        />
      )}
    </div>
  )
}
